/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect} from 'react';
import {withTranslation} from 'react-i18next';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {WarningIcon} from '@ion/dls/src/icons/WarningIcon';
import UserNotificationDialog from '@ion/dls/src/notifications/UserNotificationDialog';
import {ENVIRONMENT_QUERY, reloadDefault} from './env';
import {isPermitted, setShowNotification, showNotification} from './store';
import useEnvironmentDetails from './useEnvironmentDetails';

/**
 * Defines the properties for the EnvironmentNotification component.
 *
 * @see {@link EnvironmentNotification}
 */
export type EnvironmentNotificationProps = {
  app: string,
  param?: string
}

const useStyles = makeStyles(() => ({
  container : {
    minWidth : '600px',
    maxWidth : '600px'
  }
}));

/**
 * The `EnvironmentNotification` component provides a Notification Dialog which
 * is to be displayed to the user when an environment configuration is loaded
 * from an external domain. External `environment.json` documents are only ever
 * loaded when the `?environment=dev|qa|stg|us|eu` query parameter is provided,
 * which, if so, this component will automatically be rendered and displayed to
 * the user.
 *
 * Applications which utilize this component are to provide the following translation
 * entries in `locales/{lng}notifications.json` (note 'message' newlines are for example
 * code formatting only and should not be included in the value):
 *
 * ```typescript
 *   "environment": {
 *     "title": "External Environment Warning!",
 *     "message": "You are using {{app}} in the {{host}} environment, but all Services
 *        are pointing to {{remote}} Services. Are you sure you did not accidentally provide
 *        the ?{{param}} parameter in the URL? If you are unsure, you can reload the app by
 *        clicking on \"Cancel\", otherwise, click \"Proceed\". Please proceed with Caution!",
 *     "acknowledge": "I Acknowledge",
 *     "cancel": "I Did Not Intend This",
 *     "environment": "{{remote}} Environment",
 *     "insufficient": {
 *       "title": "Insufficient External Environment Permissions",
 *       "message": "You do not have sufficient permissions to access {{app}} in {{host}}
 *       against {{remote}} Services. Please click \"Back to Login\" to be redirected to
 *       the Login Page.",
 *       "acknowledge": "Back to Login"
 *     }
 *   }
 * ```
 *
 * Once the translations have been added, the `EnvironmentNotification` component
 * should be rendered via the applications `_app.tsx` Component:
 *
 * ```typescript
 *   // _app.tsx
 *   import EnvironmentNotification from '@ion/applications/src/env/EnvironmentNotification';
 *   ...
 *
 *   return (
 *    <>
 *      ...
 *      <EnvironmentNotification app='ADM' />
 *    </>
 *   );
 * ```
 *
 */
const EnvironmentNotification = ({app, param = ENVIRONMENT_QUERY}: EnvironmentNotificationProps) => {
  const classes = useStyles();
  const config  = useEnvironmentDetails();

  const isInsufficient = isPermitted.value === false;

  useEffect(() => {
    setShowNotification(config.isExternal);
  }, [config]);

  const onAcknowledge = useCallback(() => {
    setShowNotification?.(false);
  }, []);

  const onAbort = useCallback(() => {
    reloadDefault(param);
  }, [param]);

  const getEntry = (entry: string, params?: Record<string, unknown>): string => (
    translate(
      `notifications:environment.${isInsufficient ? 'insufficient.' : ''}${entry}`,
      params
    )
  );

  if (showNotification.value || isInsufficient) {
    return (
      <div className={classes.container}>
        <UserNotificationDialog icon={<WarningIcon />}
          title={getEntry('title')}
          message={getEntry('message', {...config, app, param})}
          actionLabel={getEntry('acknowledge')}
          dismissLabel={!isInsufficient ? translate('notifications:environment.cancel') : undefined}
          onAction={!isInsufficient ? onAcknowledge : onAbort}
          onDismiss={!isInsufficient ? onAcknowledge : onAbort}
          maxWidth={600}
          capitalize
          show />
      </div>
    );
  }
  return null;
};
export default withTranslation([
  'notifications'
])(React.memo(EnvironmentNotification));
